/**
 * Client-side reading of the supplier bulk-edit result contract
 * ({ requested, skippedIds, alreadyUpToDateIds } alongside the action's count key).
 * Every accessor tolerates a response from a server that predates the contract,
 * in which case the outcome degrades to "no skip information".
 */

export type SupplierBulkResult = {
  updated?: number;
  requested?: number;
  skippedIds?: unknown;
  alreadyUpToDateIds?: unknown;
  unavailableSkippedIds?: unknown;
};

/** What a bulk run did, as reported back to the page for selection handling. */
export type SupplierBulkRunOutcome = {
  changed: number;
  skippedIds: string[];
  /** The subset of skippedIds that is deleted, inactive, or foreign — never retained. */
  unavailableSkippedIds?: string[];
  /** Keep the skipped ids selected — only for actions whose skipped rows can still render. */
  retainSkipped?: boolean;
  /** Which blacklist partition the retained skipped ids really belong to. */
  reclassify?: 'block' | 'unblock';
};

export function idListOf(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

export function skippedIdsOf(result: SupplierBulkResult | null | undefined): string[] {
  return idListOf(result?.skippedIds);
}

export function alreadyUpToDateIdsOf(result: SupplierBulkResult | null | undefined): string[] {
  return idListOf(result?.alreadyUpToDateIds);
}

export function unavailableSkippedIdsOf(result: SupplierBulkResult | null | undefined): string[] {
  return idListOf(result?.unavailableSkippedIds);
}

export type NameList = { shown: string[]; more: number };

/**
 * Display names for up to `cap` of the given ids. Names come from the caller's row
 * cache; "select all" can cover pages never visited whose rows are uncached, so ids
 * without a name are folded into `more` together with the over-cap remainder.
 */
export function buildNameList(ids: string[], getName: (id: string) => string | undefined, cap = 5): NameList {
  const names = ids.map(getName).filter((name): name is string => Boolean(name));
  const shown = names.slice(0, cap);
  return { shown, more: ids.length - shown.length };
}

/**
 * Ids to keep selected after a bulk run: exactly the skipped ones, so the user can
 * see which rows the server declined without re-selecting them. null = clear the
 * selection (nothing skipped, or the server sent no skip information).
 */
export function retainAfterSupplierBulk(
  outcome: SupplierBulkRunOutcome | null | undefined,
  currentSelectedIds: string[]
): string[] | null {
  const skipped = outcome ? outcome.skippedIds : [];
  if (!skipped.length) return null;
  const unavailable = new Set(outcome?.unavailableSkippedIds ?? []);
  const current = new Set(currentSelectedIds.map(String));
  const retained = skipped.filter(id => current.has(id) && !unavailable.has(id));
  return retained.length ? retained : null;
}

/** One settled half of an assignment run. */
export type SettledBulk = PromiseSettledResult<SupplierBulkResult | null>;

export type AreaAssignmentSummary = {
  added: number;
  removed: number;
  skippedIds: string[];
  failedDirections: Array<'add' | 'remove'>;
};

/**
 * Folds the two halves of an area-assignment run (removals, additions) into one report.
 * The halves are independent requests, so either can fail on its own — a rejected half
 * contributes no counts but is named in failedDirections, which is what lets a partial
 * run be reported as a partial run rather than a total failure.
 */
export function summarizeAreaAssignment(remove: SettledBulk, add: SettledBulk): AreaAssignmentSummary {
  const countOf = (half: SettledBulk) => (half.status === 'fulfilled' ? half.value?.updated ?? 0 : 0);
  const skipsOf = (half: SettledBulk) => (half.status === 'fulfilled' ? skippedIdsOf(half.value) : []);
  const failedDirections: Array<'add' | 'remove'> = [];
  if (remove.status === 'rejected') failedDirections.push('remove');
  if (add.status === 'rejected') failedDirections.push('add');
  return {
    removed: countOf(remove),
    added: countOf(add),
    skippedIds: Array.from(new Set([...skipsOf(remove), ...skipsOf(add)])),
    failedDirections,
  };
}
